/**
 * FASE 20.7 — COMPACT DATA SUMMARIZATION
 *
 * Converts tool execution results into a short pastoral context string
 * safe for LLM injection. Max 500 chars total across all tool results.
 *
 * INVARIANT: never inject raw rows. Always summarize.
 * INVARIANT: sanitizeRows() must have already been called (executeTool does this).
 */

const MAX_CONTEXT_CHARS = 480; // leave 20 char margin below 500

const MONTHS_SHORT = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
function shortDate(iso) {
  if (!iso) return null;
  const [, m, d] = iso.split("-");
  return `${parseInt(d, 10)} ${MONTHS_SHORT[parseInt(m, 10) - 1]}`;
}

/* ─── Per-tool formatters ──────────────────────────────────────── */

function formatTodayVisits(rows) {
  if (!rows.length) return "Sin visitas programadas para hoy.";
  const pending = rows.filter((r) => r.status === "pendiente").length;
  const done    = rows.filter((r) => r.status === "realizada").length;
  const lines   = rows.slice(0, 3).map((r) => {
    const next = r.next_visit_date ? ` (próx. ${shortDate(r.next_visit_date)})` : "";
    return r.contact_name ? `${r.contact_name}${next}` : null;
  }).filter(Boolean).join(", ");
  return `Hoy: ${rows.length} visita${rows.length !== 1 ? "s" : ""} (${pending} pendiente${pending !== 1 ? "s" : ""}, ${done} realizada${done !== 1 ? "s" : ""})${lines ? ` — ${lines}` : ""}.`;
}

function formatUpcomingEvents(rows) {
  if (!rows.length) return "Sin eventos próximos.";
  const next = rows.slice(0, 3).map((r) => {
    const typeLabel = r.type === "field" ? " [campo]" : r.type === "church" ? " [iglesia]" : "";
    return `${r.title}${typeLabel}${r.date ? ` (${r.date.slice(5)})` : ""}`;
  }).join(", ");
  return `${rows.length} evento${rows.length !== 1 ? "s" : ""} próximo${rows.length !== 1 ? "s" : ""}: ${next}.`;
}

function formatFamilyOverview(rows) {
  if (!rows.length) return null;
  const f = rows[0];
  const lastVisit = f.last_visit_date ? `última visita ${f.last_visit_date.slice(0, 10)}` : "sin visitas registradas";
  return `Familia: ${f.name || "—"} — ${f.status || ""} — ${f.member_count ?? "?"} miembro${(f.member_count ?? 0) !== 1 ? "s" : ""} — ${lastVisit}.`;
}

function formatRecentPrayers(rows) {
  if (!rows.length) return "Sin solicitudes de oración recientes.";
  const active = rows.filter((r) => r.status !== "respondida").length;
  const cats   = [...new Set(rows.slice(0, 5).map((r) => r.category).filter(Boolean))].join(", ");
  return `${rows.length} oración${rows.length !== 1 ? "es" : ""} reciente${rows.length !== 1 ? "s" : ""} (${active} activa${active !== 1 ? "s" : ""})${cats ? ` — ${cats}` : ""}.`;
}

function formatPeopleNeedingFollowup(rows) {
  if (!rows.length) return "Sin personas pendientes de seguimiento.";
  const names = rows.slice(0, 3).map((r) => r.name).filter(Boolean).join(", ");
  return `${rows.length} persona${rows.length !== 1 ? "s" : ""} sin visita reciente${names ? `: ${names}` : ""}.`;
}

function formatChurchActivity(rows) {
  if (!rows.length) return null;
  const active = rows.filter((r) => r.status === "activa").length;
  return `${rows.length} iglesia${rows.length !== 1 ? "s" : ""} activa${active !== 1 ? "s" : ""} en el distrito.`;
}

function formatMemberStatus(rows) {
  if (!rows.length) return null;
  const baptized   = rows.filter((r) => r.baptism_status === "bautizado").length;
  const catechumens = rows.filter((r) => r.baptism_status === "catecumeno");
  const catNames   = catechumens.slice(0, 4).map((r) => r.name).filter(Boolean).join(", ");
  const catCount   = catechumens.length;
  return `Membresía: ${rows.length} total — ${baptized} bautizados, ${catCount} catecúmeno${catCount !== 1 ? "s" : ""}${catNames ? `: ${catNames}` : ""}.`;
}

function formatVisitTimeline(rows) {
  if (!rows.length) return "Sin historial de visitas recientes.";
  const last = rows[0];
  return `${rows.length} visita${rows.length !== 1 ? "s" : ""} reciente${rows.length !== 1 ? "s" : ""} — última: ${last.date?.slice(0, 10) ?? "—"} (${last.status ?? "—"}).`;
}

function formatDashboardSnapshot(rows) {
  if (!rows.length) return null;
  const visits  = rows.filter((r) => r._source === "visit").length;
  const events  = rows.filter((r) => r._source === "event").length;
  const prayers = rows.filter((r) => r._source === "prayer").length;
  return `Resumen: ${visits} visita${visits !== 1 ? "s" : ""} hoy, ${events} evento${events !== 1 ? "s" : ""} próximo${events !== 1 ? "s" : ""}, ${prayers} oración${prayers !== 1 ? "es" : ""} activa${prayers !== 1 ? "s" : ""}.`;
}

function formatNextVisit(rows) {
  if (!rows.length) return "No tienes visitas pendientes programadas.";
  const v = rows[0];
  const extra    = rows.length > 1 ? ` (+${rows.length - 1} más)` : "";
  const nextDate = v.next_visit_date ? ` — próx. ${shortDate(v.next_visit_date)}` : "";
  return `Próxima visita: ${v.contact_name || "—"} — ${v.date || "—"}${v.visit_type ? ` (${v.visit_type})` : ""}${nextDate}${extra}.`;
}

function formatUpcomingPreachings(rows) {
  if (!rows.length) return "Sin predicaciones programadas próximamente.";
  const lines = rows.slice(0, 5).map((r) => {
    const date    = shortDate(r.date) ?? "—";
    const church  = r.church_name  || "iglesia";
    const preacher = r.preacher_name || "sin asignar";
    return `${date} — ${church}: ${preacher}`;
  });
  return `Próximas predicaciones: ${lines.join(". ")}.`;
}

function formatUpcomingVisits(rows) {
  if (!rows.length) return "Sin visitas pendientes en los próximos días.";
  const names = rows.slice(0, 3).map((r) => r.contact_name).filter(Boolean).join(", ");
  return `${rows.length} visita${rows.length !== 1 ? "s" : ""} pendiente${rows.length !== 1 ? "s" : ""}${names ? `: ${names}` : ""}.`;
}

function formatWeekSchedule(rows) {
  if (!rows.length) return "Sin actividad programada para esta semana.";
  const visits = rows.filter((r) => r._source === "visit").length;
  const events = rows.filter((r) => r._source === "event").length;
  const next3  = rows.slice(0, 3).map((r) => r.title || r.contact_name).filter(Boolean).join(", ");
  return `Esta semana: ${visits} visita${visits !== 1 ? "s" : ""} y ${events} evento${events !== 1 ? "s" : ""}${next3 ? ` — ${next3}` : ""}.`;
}

/* ─── Router ─────────────────────────────────────────────────── */

function formatSingle(toolId, rows) {
  switch (toolId) {
    case "get_today_visits":            return formatTodayVisits(rows);
    case "get_upcoming_events":         return formatUpcomingEvents(rows);
    case "get_family_overview":         return formatFamilyOverview(rows);
    case "get_recent_prayers":          return formatRecentPrayers(rows);
    case "get_people_needing_followup": return formatPeopleNeedingFollowup(rows);
    case "get_church_activity_summary": return formatChurchActivity(rows);
    case "get_member_status":           return formatMemberStatus(rows);
    case "get_visit_timeline":          return formatVisitTimeline(rows);
    case "get_dashboard_snapshot":      return formatDashboardSnapshot(rows);
    case "get_next_visit":              return formatNextVisit(rows);
    case "get_upcoming_visits":         return formatUpcomingVisits(rows);
    case "get_week_schedule":           return formatWeekSchedule(rows);
    case "get_upcoming_preachings":     return formatUpcomingPreachings(rows);
    default:                            return null;
  }
}

/* ─── Public API ─────────────────────────────────────────────── */

/**
 * Build a compact [data: ...] context block from one or more tool results.
 * Returns null if no useful content.
 *
 * @param {Array<{ ok: boolean, tool: string, rows: Object[] }>} results
 * @returns {string|null}
 */
export function compactDataContext(results) {
  if (!results?.length) return null;

  const lines = [];

  for (const result of results) {
    if (!result.ok) continue;
    const line = formatSingle(result.tool, result.rows ?? []);
    if (line) lines.push(line);
  }

  if (!lines.length) return null;

  const body = lines.join(" ").slice(0, MAX_CONTEXT_CHARS);
  return `[data: ${body}]`;
}
